import { useState, useEffect } from 'react';
import * as theme from '../../../theme';
import dayjs from 'dayjs';
import TruCard from '../../TruCard';
import OnboardingItem from '../items/OnboardingItem';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from "react-router";
import { db } from '../../../firebase';
import { updateOnboardingStep } from '../../../functions/updateOnboarding';
import getServerTimestamp from '../../../functions/endpoints/GetServerTimestamp';
import ConfirmationModal from '../../ConfirmationModal';

export default function OnboardingList({ practitionerId, onboarding, currentUser, setError, setPractitioner, completedOnboarding }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);

  // Check if all onboarding tasks are complete
  useEffect(() => {
    const checkOnboardingComplete = async () => {
      if (completedOnboarding) return; // Skip if already complete

      const allTasksComplete = (
        onboarding.training?.dateCompleted &&
        onboarding.form?.dateCompleted &&
        onboarding.scheduleCall?.dateScheduled &&
        onboarding.completeCall?.dateCompleted &&
        onboarding.cardOnFile?.dateCompleted &&
        onboarding.returnKit?.dateCompleted &&
        onboarding.personalScanReview?.dateCompleted
      );

      if (allTasksComplete) {
        try {
          const timestamp = await getServerTimestamp();
          const practitionerRef = doc(db, 'practitioners', practitionerId);

          await updateDoc(practitionerRef, {
            completedOnboarding: true,
            dateModified: timestamp,
          });

          // Update local state
          setPractitioner(prev => ({
            ...prev,
            completedOnboarding: true,
            dateModified: timestamp,
          }));
        } catch (err) {
          setError(err.message);
        }
      }
    };

    checkOnboardingComplete();
  }, [onboarding, completedOnboarding, practitionerId, setPractitioner, setError]);

  const handleAction = (action) => {
    setCurrentAction(action);
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    setModalOpen(false);

    try {
      const { success, practitioner } = await updateOnboardingStep({
        practitionerId,
        step: currentAction,
      });

      if (success) {
        setPractitioner(practitioner);
      } else {
        throw new Error('Failed to update onboarding step');
      }
    } catch (err) {
      setError(`Failed to update onboarding step: ${err.message}`);
    }
  };

  const getModalConfig = () => {
    switch (currentAction) {
      case 'scheduleCall':
        return {
          title: 'Confirm Call Scheduled',
          description: 'Confirm that the onboarding call has been scheduled.',
          confirmButtonText: 'Confirm',
        };
      case 'scheduleCallModal':
        return {
          title: 'Onboarding Call Request',
          description: 'A member of the TruBalance team will reach out to schedule your 1:1 Onboarding Call now that you have completed the training and onboarding form.',
          confirmButtonText: 'Close',
          hideCancelButton: true,
        };
      case 'completeCall':
        return {
          title: 'Confirm Call Completed',
          description: 'Confirm that the onboarding call has been completed.',
          confirmButtonText: 'Confirm',
        };
      case 'returnKit':
        return {
          title: 'Confirm Practitioner Kit Returned',
          description: 'Confirm that the practitioner kit has been returned.',
          confirmButtonText: 'Confirm',
        };
      case 'personalScanReview':
        return {
          title: 'Confirm Personal Scan Review Call Completed',
          description: 'Confirm that the personal scan review call has been completed.',
          confirmButtonText: 'Confirm',
        };
      default:
        return {};
    }
  };

  const videoLinkProps = currentUser.role === 'practitioner' 
    ? { actionLink: () => navigate(`/training/${practitionerId}`) } 
    : {};

  const scheduleCallProps = currentUser.role === 'practitioner' && (onboarding.form && onboarding.training)
    ? { actionLink: onboarding.form && !onboarding.scheduleCall ? () => navigate(`/practitioners/${practitionerId}/schedule-call`) : null } 
    : {};

  const cardOnFileProps = currentUser.role === 'practitioner' 
    ? { actionLink: () => navigate(`/practitioners/${practitionerId}/billing`) } 
    : {};

  return (
    <TruCard title={'Onboarding Checklist'}>
      <OnboardingItem
        action="videos"
        icon="video"
        label="Complete Training Videos"
        isComplete={onboarding.training && onboarding.training.dateCompleted}
        content={onboarding.training && onboarding.training.dateCompleted ? dayjs(onboarding.training.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        {...videoLinkProps}
      />
      <OnboardingItem
        action="form"
        icon="report"
        label="Complete Onboarding Form"
        isComplete={onboarding.form && onboarding.form.dateCompleted}
        content={onboarding.form && onboarding.form.dateCompleted ? dayjs(onboarding.form.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        actionLink={() => onboarding.form ? navigate(`/practitioners/${practitionerId}/onboarding`) : navigate(`/practitioners/${practitionerId}/onboarding/add`)}
      />
      <OnboardingItem
        action="scheduleCall"
        icon="calendar"
        label="Request Onboarding Call"
        disabled={!onboarding.form || !onboarding.training}
        nextUp={!onboarding.scheduleCall && onboarding.form && onboarding.training}
        content={onboarding.scheduleCall && onboarding.scheduleCall.dateScheduled ? dayjs(onboarding.scheduleCall.dateScheduled.toDate()).format('MM/DD/YYYY h:mm A') : null}
        isComplete={onboarding.scheduleCall && onboarding.scheduleCall.dateScheduled}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        onComplete={handleAction}
        {...scheduleCallProps}
      />
      <OnboardingItem
        action="completeCall"
        icon="call"
        label="Complete 1:1 Onboarding Call"
        disabled={!onboarding.scheduleCall}
        nextUp={!onboarding.completeCall && onboarding.scheduleCall}
        content={onboarding.completeCall && onboarding.completeCall.dateCompleted ? dayjs(onboarding.completeCall.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        isComplete={onboarding.completeCall && onboarding.completeCall.dateCompleted}
        canComplete={true}
        onComplete={handleAction}
      />
      <OnboardingItem
        action="cardOnFile"
        icon="money"
        label="Setup Card on File"
        isComplete={onboarding.cardOnFile && onboarding.cardOnFile.dateCompleted}
        content={onboarding.cardOnFile && onboarding.cardOnFile.dateCompleted ? dayjs(onboarding.cardOnFile.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        {...cardOnFileProps}
      />
      <OnboardingItem
        action="returnKit"
        icon="mail"
        label="Return Practitioner Kit"
        disabled={!onboarding.completeCall}
        nextUp={!onboarding.returnKit && onboarding.completeCall}
        content={onboarding.returnKit && onboarding.returnKit.dateCompleted ? dayjs(onboarding.returnKit.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        isComplete={onboarding.returnKit && onboarding.returnKit.dateCompleted}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        onComplete={handleAction}
      />
      <OnboardingItem
        action="personalScanReview"
        icon="call"
        label="Personal Scan Review Call"
        disabled={!onboarding.returnKit}
        nextUp={!onboarding.personalScanReview && onboarding.returnKit}
        content={onboarding.personalScanReview && onboarding.personalScanReview.dateCompleted ? dayjs(onboarding.personalScanReview.dateCompleted.toDate()).format('MM/DD/YYYY h:mm A') : null}
        isComplete={onboarding.personalScanReview && onboarding.personalScanReview.dateCompleted}
        canComplete={true}
        onComplete={handleAction}
      />
      <ConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={currentAction === 'scheduleCallModal' ? () => setModalOpen(false) : handleConfirm}
        {...getModalConfig()}
      />
    </TruCard>
  );
}